import React, { useEffect, useRef } from 'react';
import './Turnstile.scss';

/**
 * Cloudflare Turnstile widget (non-interactive / managed mode is decided in the Cloudflare dashboard).
 * Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is not configured so local dev keeps working;
 * the API only enforces the token when TURNSTILE_ENFORCE is on.
 */

export const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || process.env.REACT_TURNSTILE_SITE_KEY || '').trim();
export const TURNSTILE_ENABLED = !!TURNSTILE_SITE_KEY;

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileRenderOptions {
    sitekey: string;
    action?: string;
    theme?: 'light' | 'dark' | 'auto';
    size?: 'normal' | 'flexible' | 'compact';
    appearance?: 'always' | 'execute' | 'interactive-only';
    language?: string;
    callback?: (token: string) => void;
    'expired-callback'?: () => void;
    'error-callback'?: (code?: string) => void;
    'timeout-callback'?: () => void;
}

interface TurnstileApi {
    render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
    reset: (widgetId?: string) => void;
    remove: (widgetId?: string) => void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    if (!scriptPromise) {
        scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                if (window.turnstile) {
                    resolve(window.turnstile);
                } else {
                    reject(new Error('Turnstile script loaded without window.turnstile'));
                }
            };
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error('Could not load Turnstile script'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

export interface TurnstileHandle {
    /** Discards the current token and requests a new challenge (tokens are single use). */
    reset: () => void;
}

interface TurnstileProps {
    /** Identifies the form on the Cloudflare side, e.g. "login" | "register" */
    action: string;
    /** Receives the token when solved, and '' when it expires or errors */
    onToken: (token: string) => void;
    size?: 'normal' | 'flexible' | 'compact';
}

const Turnstile = React.forwardRef<TurnstileHandle, TurnstileProps>(({ action, onToken, size = 'flexible' }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    React.useImperativeHandle(ref, () => ({
        reset: () => {
            onTokenRef.current('');
            if (window.turnstile && widgetIdRef.current) {
                try {
                    window.turnstile.reset(widgetIdRef.current);
                } catch (e) {
                    console.warn('<Turnstile> reset failed: ', e);
                }
            }
        },
    }), []);

    useEffect(() => {
        if (!TURNSTILE_ENABLED || !containerRef.current) {
            return;
        }
        let disposed = false;
        loadTurnstile()
            .then((turnstile) => {
                if (disposed || !containerRef.current) {
                    return;
                }
                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    action,
                    theme: 'light',
                    size,
                    language: 'es',
                    appearance: 'always',
                    callback: (token) => onTokenRef.current(token),
                    'expired-callback': () => onTokenRef.current(''),
                    'timeout-callback': () => onTokenRef.current(''),
                    'error-callback': (code) => {
                        console.warn('<Turnstile> widget error: ', code);
                        onTokenRef.current('');
                    },
                });
            })
            .catch((err) => {
                console.error('<Turnstile> ', err);
                onTokenRef.current('');
            });
        return () => {
            disposed = true;
            if (window.turnstile && widgetIdRef.current) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch (e) {
                    // Widget already gone
                }
                widgetIdRef.current = null;
            }
        };
    }, [action, size]);

    if (!TURNSTILE_ENABLED) {
        return null;
    }
    return <div className="_turnstile" ref={containerRef} />;
});

export default Turnstile;
